import { Injectable } from "@angular/core";

import { RemoteGatewayFactory } from "../../gateways/remote-gateway-factory";
import { BackendUrl } from "../../models/domains/urls/back-end.url";
import { BenefitConfigurationParser } from "../parsers/pbm/benefit/benefit-configuration.parser";
import { BenefitConfiguration } from "../../models/domains/pbm/Benefit/benefit-configuration";
import { ProductConfigurationParser } from "../parsers/pbm/benefit/product-configuration.parser";
import { NetworkProductConfigurationParser } from "../parsers/pbm/benefit/network-product-configuration.parser";
import { NetworkProductConfigurationCluster } from "../../models/domains/pbm/Benefit/network-product-configuration";
import { TransactionConfigurationParser } from "../parsers/pbm/benefit/transaction-configuration.parser";
import { TransactionConfiguration } from "./../../models/domains/pbm/Benefit/transaction-configuration";
import { MedicalPrescriptionConfigurationParser } from "../parsers/pbm/benefit/medical-prescription-configuration.parser";
import { MedicalPrescriptionConfiguration } from "../../models/domains/pbm/Benefit/medical-prescription-configuration";
import { LifeCycleConfigurationParser } from "../parsers/pbm/benefit/life-cycle-configuration.parser";
import { LifeCycleConfiguration } from "../../models/domains/pbm/Benefit/life-cycle-configuration";
import { ProductConfigurationCluster } from "../../models/domains/pbm/Benefit/product-configuration-cluster";
import { ProductBlockItem } from "../../models/domains/pbm/Benefit/product-block-item";
import { TherapeuticClassBlockItem } from "../../models/domains/pbm/Benefit/therapeutic-class-block-item";
import { BlockItemType } from "../../models/domains/pbm/Benefit/block-item-type";
import { MedicalPrescriptionMappingType } from "../../models/domains/pbm/Benefit/medical-prescription-mapping-type";
import { MedicalPrescriptionMappingConfiguration } from "../../models/domains/pbm/Benefit/medical-prescription-mapping-configuration";
import { TherapeuticClassMedicalPrescriptionMappingConfiguration } from "../../models/domains/pbm/Benefit/therapeutic-class-medical-prescription-mapping-configuration";
import { PagingRequest } from "../../models/domains/paginator/paging-request";
import { PageableResult } from "../../models/domains/paginator/pageable-result";
import { SearchCompanyParser } from "../parsers/pbm/company/search-company.parser";
import { BeneficiaryConfiguration } from "../../models/domains/pbm/Benefit/beneficiary-configuration";
import { BeneficiaryConfigurationParser } from "../parsers/pbm/benefit/beneficiary-configuration.parser";
import { UserRoleMediator } from "../mediators/user-role.mediator";

@Injectable()
export class BenefitRepository {
  constructor(
    private remoteGatewayFactory: RemoteGatewayFactory,
    private benefitConfigurationParser: BenefitConfigurationParser,
    private productConfigurationParser: ProductConfigurationParser,
    private networkProductConfigurationParser: NetworkProductConfigurationParser,
    private transactionConfigurationParser: TransactionConfigurationParser,
    private medicalPrescriptionConfigurationParser: MedicalPrescriptionConfigurationParser,
    private beneficiaryConfigurationParser: BeneficiaryConfigurationParser,
    private lifecycleConfigurationParser: LifeCycleConfigurationParser,
    private searchCompanyParser: SearchCompanyParser,
    private userRoleMediator: UserRoleMediator,
  ) {}

  public async searchCompany(fiscalNumber: string, pagingRequest: PagingRequest): Promise<PageableResult> {
    const body = {
      StartIndex: pagingRequest.getStart(),
      TotalPerPages: pagingRequest.getEnd(),
      FilterText: fiscalNumber,
    };

    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };

    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/benefit/configurations/companies"), body, headers);
      return this.searchCompanyParser.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async saveBenefitConfiguration(benefitConfiguration: BenefitConfiguration): Promise<any> {
    const headers = { "Content-Type": "application/json" };
    const body = JSON.stringify(benefitConfiguration);
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/benefit/configurations");
      return await remoteGateway.postWithHeaders(backEndUrl, body, headers);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async findBenefitConfigurationByFiscalNumber(fiscalNumber: string): Promise<BenefitConfiguration> {
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/benefit/configurations/" + fiscalNumber), headers);
      return this.benefitConfigurationParser.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async saveProductConfiguration(productConfigurationCluster: ProductConfigurationCluster): Promise<any> {
    const headers = { "Content-Type": "application/json" };
    const productConfigurationList = productConfigurationCluster.getProductConfiguration().map((configuration) => {
      const productConfigurationItem = new ProductConfigurationItem();
      productConfigurationItem.setProductTypeKey(configuration.getProductType().getKey());
      productConfigurationItem.setProductSubtypeKey(configuration.getProductSubType().getKey());
      productConfigurationItem.setPurchaseEnabled(configuration.getPurchaseEnabled());
      productConfigurationItem.setSubsidyEnabled(configuration.getSubsidyEnabled());
      productConfigurationItem.setSubsidy(configuration.getSubsidy());
      productConfigurationItem.setDiscountEnabled(configuration.getDiscountEnabled());
      productConfigurationItem.setDiscountPercentValue(configuration.getDiscountPercentValue());
      productConfigurationItem.setFormPayment(configuration.getFormPayment());

      return productConfigurationItem;
    });
    const body = JSON.stringify({
      Key: productConfigurationCluster.getKey(),
      CompanyFiscalNumber: productConfigurationCluster.getCompanyFiscalNumber(),
      ProductConfiguration: productConfigurationList,
      ProductClusterExceptInfo: productConfigurationCluster.getProductClusterExceptInfo(),
      ProductMaxCount: productConfigurationCluster.getProductMaxCount(),
      ProductFrequency: productConfigurationCluster.getProductFrequency(),
      ProductRepeatPurchase: productConfigurationCluster.getProductRepeatPurchase(),

      BlockListConfiguration: {
        BlockListItems: productConfigurationCluster.getBlockListConfiguration().map((item) => {
          if (item.hasBlockItemBy(BlockItemType.Product)) {
            const product = item as unknown as ProductBlockItem;
            return {
              BlockListItemType: 1,
              ProductKey: product.getProductKey(),
              Ean: product.getEan(),
              ProductName: product.getProductName(),
              TherapeuticClassKey: "",
              TherapeuticClass: "",
              TherapeuticClassCode: "",
            };
          } else if (item.hasBlockItemBy(BlockItemType.TherapeuticClass)) {
            const therapeuticClass = item as unknown as TherapeuticClassBlockItem;
            return {
              BlockListItemType: 2,
              ProductKey: "",
              Ean: "",
              ProductName: "",
              TherapeuticClassKey: therapeuticClass.getTherapeuticClassKey(),
              TherapeuticClass: therapeuticClass.getDescription(),
              TherapeuticClassCode: therapeuticClass.getCode(),
            };
          }
        }),
      },
      ProductClusterManagement: {
        ClustersForManagement: productConfigurationCluster.getClustersForManagement(),
      },
    });

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/benefit/configurations/product");
      if (productConfigurationCluster.isNew()) {
        return await remoteGateway.postWithHeaders(backEndUrl, body, headers);
      } else {
        return await remoteGateway.putWithHeaders(backEndUrl, body, headers);
      }
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async findProductConfigurationByFiscalNumber(fiscalNumber: string): Promise<ProductConfigurationCluster> {
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/benefit/configurations/product/" + fiscalNumber), headers);
      return this.productConfigurationParser.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async saveNetworkProductConfiguration(networkConfiguration: NetworkProductConfigurationCluster): Promise<any> {
    const headers = {
      "Content-Type": "application/json",
    };
    const body = JSON.stringify({
      Key: networkConfiguration.getKey(),
      CompanyFiscalNumber: networkConfiguration.getCompanyFiscalNumber(),
      AllNetworks: networkConfiguration.getAllNetworks(),
      NetworkFiscalNumbers: networkConfiguration.getAllNetworks() ? [] : networkConfiguration.getNetworkSelectedList().map((network) => network.getFiscalNumber()),
      PriceIntegration: networkConfiguration.getPriceIntegration(),
    });
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/benefit/configurations/network-product");
      if (networkConfiguration.isNew()) {
        return await remoteGateway.postWithHeaders(backEndUrl, body, headers);
      } else {
        return await remoteGateway.putWithHeaders(backEndUrl, body, headers);
      }
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async findNetworkProductConfigurationByFiscalNumber(fiscalNumber: string): Promise<NetworkProductConfigurationCluster> {
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/benefit/configurations/network-product/" + fiscalNumber), headers);
      return this.networkProductConfigurationParser.parse(data);
    } catch (error) {
      console.error(error);
      return await Promise.reject(error);
    }
  }

  public async saveTransactionConfiguration(transactionConfiguration: TransactionConfiguration): Promise<any> {
    const headers = { "Content-Type": "application/json" };

    const jobPositionConfigurations = transactionConfiguration.getJobDescriptionLimitMappingConfiguration()
      ? transactionConfiguration
          .getJobDescriptionLimitMappingConfiguration()
          .getJobDescriptionLimits()
          .map((limitConfiguration) => {
            return {
              JobDescription: {
                Key: {
                  Value: limitConfiguration.getJobDescription().getKey(),
                },
                Title: limitConfiguration.getJobDescription().getTitle(),
                IsGeneric: limitConfiguration.getJobDescription().getIsGeneric(),
                Enabled: limitConfiguration.getJobDescription().getEnabled(),
              },
              Limit: limitConfiguration.getLimit(),
              unlimited: limitConfiguration.getisUnlimited(),
            };
          })
      : [];

    const beneficiarySocialNumberLimitConfigurations = transactionConfiguration.getSocialNumberLimitMappingConfiguration()
      ? transactionConfiguration
          .getSocialNumberLimitMappingConfiguration()
          .getSocialNumberLimits()
          .map((limitConfiguration) => {
            return {
              Name: limitConfiguration.getName(),
              SocialNumber: {
                Value: limitConfiguration.getSocialNumber(),
              },
              Limit: limitConfiguration.getLimit(),
              Unlimited: limitConfiguration.getisUnlimited(),
            };
          })
      : [];

    const body = JSON.stringify({
      Key: transactionConfiguration.getKey(),
      CompanyFiscalNumber: transactionConfiguration.getCompanyFiscalNumber(),
      LimitTypeMappingConfiguration: {
        LimitType: transactionConfiguration.getLimitTypeMappingConfiguration().getLimitType().value,
        Limit: transactionConfiguration.getLimitTypeMappingConfiguration().getLimit(),
        IsCashPurchase : transactionConfiguration.getLimitTypeMappingConfiguration().getIsCashPurchase(),
      },
      JobDescriptionLimitMappingConfiguration: {
        JobPositionConfigurations: jobPositionConfigurations,
      },
      BeneficiarySocialNumberLimitMappingConfiguration: {
        BeneficiarySocialNumberLimitConfigurations: beneficiarySocialNumberLimitConfigurations,
      },
    });

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/benefit/configurations/transaction");
      if (transactionConfiguration.isNew()) {
        return await remoteGateway.postWithHeaders(backEndUrl, body, headers);
      } else {
        return await remoteGateway.putWithHeaders(backEndUrl, body, headers);
      }
    } catch (responseError) {
      if (responseError.error.Data.ErrorMessage) return await Promise.reject(responseError.error.Data.ErrorMessage);
      return await Promise.reject("Erro ao salvar configuração");
    }
  }

  public async findTransactionByFiscalNumber(fiscalNumber: string): Promise<TransactionConfiguration> {
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/benefit/configurations/transaction/" + fiscalNumber), headers);
      return this.transactionConfigurationParser.parse(data);
    } catch (referenceError) {
      const errorMessage = referenceError.error.Data.ErrorMessage;
      return await Promise.reject(errorMessage);
    }
  }

  public async saveMedicalPrescriptionConfiguration(medicalPrescriptionConfiguration: MedicalPrescriptionConfiguration): Promise<any> {
    const headers = {
      "Content-Type": "application/json",
    };
    const medicalPrescriptionMappingConfiguration = medicalPrescriptionConfiguration.getMedicalPrescriptionMappingConfiguration().map((item) => {
      const medicalPrescriptionMappingConfiguration = item as MedicalPrescriptionMappingConfiguration;
      return {
        MedicalPrescriptionValidityKey: medicalPrescriptionMappingConfiguration.getMedicalPrescriptionValidity().getKey(),
        ValidDaysPeriod: medicalPrescriptionMappingConfiguration.getValidDaysPeriod(),
      };
    });
    const body = JSON.stringify({
      Key: medicalPrescriptionConfiguration.getKey(),
      CompanyFiscalNumber: medicalPrescriptionConfiguration.getCompanyFiscalNumber(),
      Enabled: medicalPrescriptionConfiguration.getEnabled(),
      RequiredScan: medicalPrescriptionConfiguration.getRequiredScan(),
      PurchaseRequiredScan: medicalPrescriptionConfiguration.getPurchaseRequiredScan(),
      MedicalPrescriptionMappingConfiguration: medicalPrescriptionMappingConfiguration,
    });
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/benefit/configurations/medical-prescription");

      if (medicalPrescriptionConfiguration.isNew()) {
        return await Promise.resolve(remoteGateway.postWithHeaders(backEndUrl, body, headers));
      } else {
        return await Promise.resolve(remoteGateway.putWithHeaders(backEndUrl, body, headers));
      }
    } catch (referenceError) {
      const errorMessage = referenceError.error.Data.ErrorMessage;
      return await Promise.reject(errorMessage);
    }
  }

  public async findMedicalPrescriptionConfigurationByFiscalNumber(fiscalNumber: string): Promise<MedicalPrescriptionConfiguration> {
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();

      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/benefit/configurations/medical-prescription/" + fiscalNumber), headers);

      return await Promise.resolve(this.medicalPrescriptionConfigurationParser.parse(data));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async findBeneficiaryConfiguration(fiscalNumber: string): Promise<BeneficiaryConfiguration> {
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const payload = await remoteGateway.getWithHeaders(new BackendUrl("/api/benefit/configurations/beneficiary-benefit/" + fiscalNumber), headers);
      return await Promise.resolve(this.beneficiaryConfigurationParser.parse(payload));
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async saveBeneficiaryConfiguration(beneficiaryConfiguration: BeneficiaryConfiguration): Promise<any> {
    const body = JSON.stringify(beneficiaryConfiguration);
    const headers = {
      "Content-Type": "application/json",
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/benefit/configurations/beneficiary-benefit");
      if (beneficiaryConfiguration.isNew()) {
        return await Promise.resolve(remoteGateway.postWithHeaders(backEndUrl, body, headers));
      } else {
        return await Promise.resolve(remoteGateway.putWithHeaders(backEndUrl, body, headers));
      }
    } catch (referenceError) {
      const errorMessage = referenceError.error.Data.ErrorMessage;
      return await Promise.reject(errorMessage);
    }
  }

  public async saveLifeCycleConfiguration(lifeCycleConfiguration: LifeCycleConfiguration): Promise<any> {
    const body = JSON.stringify(lifeCycleConfiguration);
    const headers = {
      "Content-Type": "application/json",
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const backEndUrl = new BackendUrl("/api/benefit/configurations/life-cycle");
      
      if (lifeCycleConfiguration.isNew()) {
        return await remoteGateway.postWithHeaders(backEndUrl, body, headers);
      } else {
        return await remoteGateway.putWithHeaders(backEndUrl, body, headers);
      }
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async findLifeCycleByFiscalNumber(fiscalNumber: string): Promise<LifeCycleConfiguration> {
    const headers = {
      "Content-Type": "application/json",
      "X-CompanyFiscalNumberRoot-Filter": this.userRoleMediator.getCompanyFiscalNumberRoot(),
    };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/benefit/configurations/life-cycle/" + fiscalNumber), headers);
      return this.lifecycleConfigurationParser.parse(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async exportPDF(fiscalNumber: string): Promise<any> {
    const headers = { "Content-Type": "application/json" };
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      return await remoteGateway.downloadAsGet(new BackendUrl("/api/benefit/configurations/export/" + fiscalNumber), headers);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  
}

class ProductConfigurationItem {
  private productTypeKey: string;
  private productSubTypeKey: string;
  private purchaseEnabled: boolean;
  private subsidyEnabled: boolean;
  private subsidy: number;
  private discountEnabled: boolean;
  private discountPercentValue: number;
  private formPayment: string;

  setProductTypeKey(typeKey: string): void {
    this.productTypeKey = typeKey;
  }

  getProductTypeKey(): string {
    return this.productTypeKey;
  }

  setProductSubtypeKey(subtypeKey: string): void {
    this.productSubTypeKey = subtypeKey;
  }

  getProductSubtypeKey(): string {
    return this.productSubTypeKey;
  }

  setPurchaseEnabled(permission: boolean): void {
    this.purchaseEnabled = permission;
  }

  getPurchaseEnabled(): boolean {
    return this.purchaseEnabled;
  }

  setSubsidyEnabled(permission: boolean): void {
    this.subsidyEnabled = permission;
  }

  getSubsidyEnabled(): boolean {
    return this.subsidyEnabled;
  }

  setSubsidy(value: number): void {
    this.subsidy = value;
  }

  getSubsidy(): number {
    return this.subsidy;
  }

  setDiscountEnabled(permission: boolean): void {
    this.discountEnabled = permission;
  }

  getDiscountEnabled(): boolean {
    return this.discountEnabled;
  }

  setDiscountPercentValue(value: number): void {
    this.discountPercentValue = value;
  }

  getDiscountPercentValue(): number {
    return this.discountPercentValue;
  }

  setFormPayment(value: string): void {
    this.formPayment = value;
  }

  getFormPayment(): string {
    return this.formPayment;
  }
}

class JobPositionConfigurationItem {
  private JobPosition: number;
  private Limit: number;

  setJobPosition(jobPosition: number): void {
    this.JobPosition = jobPosition;
  }

  getJobPosition(): number {
    return this.JobPosition;
  }

  setLimit(limit: number): void {
    this.Limit = limit;
  }

  getLimit(): number {
    return this.Limit;
  }
}
